import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { IoCloseOutline } from "react-icons/io5";
import { IoMdArrowBack } from "react-icons/io";
import API_URL from "../../src/Config";
import "../forms/voyageForm.css";
import "./excavationReviewerConfiguration.css";
import {
  EXCAVATION_PERMIT_LABEL,
  buildReviewerPayload,
  buildReviewerRows,
  buildUserOptions,
  emptyReviewerRow,
  findExcavationPermitType,
  getReviewerUserId,
  validateReviewerRows,
} from "./excavationApprovalSetupUtils";

function EditExcavationReviewerConfiguration({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { departmentId } = useParams();
  const [permitOption, setPermitOption] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [rows, setRows] = useState([emptyReviewerRow()]);
  const [deletedReviewerIds, setDeletedReviewerIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const fetchConfiguration = useCallback(async () => {
    try {
      setPageLoading(true);
      const authKey = localStorage.getItem("authKey");
      const [permitResponse, userResponse] = await Promise.all([
        axios.get(`${API_URL}/api/get_work_permit_types`, {
          headers: { Authorization: authKey },
        }),
        axios.get(`${API_URL}/api/get_user`, {
          headers: { Authorization: authKey },
        }),
      ]);

      const excavationPermit = findExcavationPermitType(
        permitResponse.data?.work_permit_types || []
      );

      if (!excavationPermit) {
        setSeverity("error");
        showSnackbar("Excavation permit type was not found.");
        return;
      }

      setPermitOption({
        value: excavationPermit.id,
        label: EXCAVATION_PERMIT_LABEL,
      });

      const setupResponse = await axios.get(
        `${API_URL}/api/permit/departments/setup/${excavationPermit.id}`,
        { headers: { Authorization: authKey } }
      );
      const departments = setupResponse.data?.departments || [];
      const currentDepartment = departments.find(
        (department) => String(department.department_id) === String(departmentId)
      );

      setSelectedDepartment(
        currentDepartment
          ? {
              value: currentDepartment.department_id,
              label: currentDepartment.department_name,
            }
          : null
      );
      setUserOptions(buildUserOptions(userResponse.data || []));

      if (departmentId) {
        const reviewerResponse = await axios.get(
          `${API_URL}/api/permit/departments/reviewers/${departmentId}`,
          { headers: { Authorization: authKey } }
        );
        setRows(buildReviewerRows(reviewerResponse.data?.reviewers || []));
      }
    } catch (error) {
      console.error("Error fetching reviewer configuration:", error.message);
      setSeverity("error");
      showSnackbar("Unable to fetch reviewer configuration.");
    } finally {
      setPageLoading(false);
    }
  }, [departmentId, showSnackbar]);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  const handleReviewerChange = (index, selectedOption) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              user_id: selectedOption ? getReviewerUserId(selectedOption) : "",
              user_name: selectedOption ? selectedOption.user_name : "",
              user_email: selectedOption ? selectedOption.user_email : "",
            }
          : row
      )
    );
    setErrors((prevErrors) => ({ ...prevErrors, [`user_id_${index}`]: "" }));
  };

  const handleRowChange = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, emptyReviewerRow()]);
  };

  const handleRemoveRow = (index) => {
    const row = rows[index];
    if (row?.reviewer_id) {
      setDeletedReviewerIds((prevIds) => [...prevIds, row.reviewer_id]);
    }
    setRows((prevRows) =>
      prevRows.filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const validateForm = () => {
    const validationErrors = validateReviewerRows(rows);
    if (!selectedDepartment?.value) {
      validationErrors.department_id = "Department is required.";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) {
      setSeverity("error");
      showSnackbar("Please fix the highlighted fields.");
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const headers = { Authorization: authKey };
      const normalizedRows = rows.map((row) => ({
        ...row,
        user_id: getReviewerUserId(row),
      }));
      const activeRows = normalizedRows.filter((row) => row.user_id);
      const isSameId = (left, right) => String(left || "") === String(right || "");
      const changedExistingRows = activeRows.filter(
        (row) =>
          row.reviewer_id && !isSameId(row.user_id, row.original_user_id)
      );
      const permissionChangedRows = activeRows.filter(
        (row) =>
          row.reviewer_id &&
          isSameId(row.user_id, row.original_user_id) &&
          (String(row.is_primary) !== String(row.original_is_primary) ||
            String(row.can_approve) !== String(row.original_can_approve) ||
            String(row.can_reject) !== String(row.original_can_reject))
      );
      const newRows = activeRows.filter((row) => !row.reviewer_id);
      const reviewerIdsToDelete = Array.from(
        new Set(
          [...deletedReviewerIds, ...changedExistingRows.map((row) => row.reviewer_id)]
            .filter(Boolean)
            .map((reviewerId) => String(reviewerId))
        )
      );

      await Promise.all(
        reviewerIdsToDelete.map((reviewerId) =>
          axios.delete(
            `${API_URL}/api/permit/departments/reviewers/${reviewerId}`,
            { headers }
          )
        )
      );

      await Promise.all(
        permissionChangedRows.map((row) =>
          axios.put(
            `${API_URL}/api/permit/departments/reviewers/${row.reviewer_id}`,
            {
              is_primary: Number(row.is_primary),
              can_approve: Number(row.can_approve),
              can_reject: Number(row.can_reject),
            },
            { headers }
          )
        )
      );

      const rowsToCreate = [...newRows, ...changedExistingRows];
      if (rowsToCreate.length) {
        await axios.post(
          `${API_URL}/api/permit/departments/reviewers`,
          buildReviewerPayload(selectedDepartment.value, rowsToCreate),
          { headers }
        );
      }

      if (
        !reviewerIdsToDelete.length &&
        !permissionChangedRows.length &&
        !rowsToCreate.length
      ) {
        setSeverity("success");
        showSnackbar("No reviewer changes to update");
        setTimeout(() => navigate("/excavation-reviewer-configuration"), 1000);
        return;
      }

      setSeverity("success");
      showSnackbar("Reviewer configuration updated successfully");
      setTimeout(() => navigate("/excavation-reviewer-configuration"), 1000);
    } catch (error) {
      console.error("Error updating reviewer configuration:", error.message);
      setSeverity("error");
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while updating reviewer configuration."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/excavation-reviewer-configuration");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveClick();
    }
  };

  return (
    <>
      <div
        className={`data-edit-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-edit-form-content">
          <div className="data-edit-form-header">
            <div className="data-edit-form-header-content">
              <div className="data-edit-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={handleCancelClick}
                  />
                </Tooltip>
                Edit Excavation Reviewer Configuration
              </div>
              <div className="data-edit-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-edit-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form onKeyDown={handleKeyDown}>
                <div className="new-data-edit-form excavation-reviewer-form">
                  <div className="form-group-select excavation-reviewer-form-row">
                    <label>
                      Permit Type<span className="text-danger">*</span>
                    </label>
                    <Select
                      className="excavation-reviewer-field"
                      options={permitOption ? [permitOption] : []}
                      value={permitOption}
                      isDisabled
                      placeholder="Excavation"
                    />
                  </div>

                  <div className="form-group excavation-reviewer-form-row">
                    <label>
                      Department<span className="text-danger">*</span>
                    </label>
                    <div className="input-group excavation-reviewer-input-group">
                      <input
                        className="excavation-reviewer-input"
                        type="text"
                        value={selectedDepartment?.label || ""}
                        disabled
                        placeholder="Department"
                      />
                      {errors.department_id && (
                        <div className="error-message">
                          {errors.department_id}
                        </div>
                      )}
                    </div>
                  </div>

                  {rows.map((row, index) => (
                    <div
                      className="excavation-reviewer-card"
                      key={`reviewer-row-${index}`}
                    >
                      <div className="excavation-reviewer-card-header">
                        Reviewer {index + 1}
                      </div>
                      <div className="form-group-select excavation-reviewer-card-row">
                        <label>
                          Reviewer
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          className="excavation-reviewer-field"
                          options={userOptions}
                          value={
                            userOptions.find(
                              (option) => option.value === row.user_id
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleReviewerChange(index, selectedOption)
                          }
                          placeholder="Select reviewer"
                        />
                        {errors[`user_id_${index}`] && (
                          <div className="error-message">
                            {errors[`user_id_${index}`]}
                          </div>
                        )}
                      </div>

                      <div className="checkbox-form-group excavation-reviewer-card-row">
                        <label className="checkbox-form-group-label">
                          Permissions
                        </label>
                        <div className="fuel-checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={row.is_primary === "1"}
                              onChange={(event) =>
                                handleRowChange(
                                  index,
                                  "is_primary",
                                  event.target.checked ? "1" : "0"
                                )
                              }
                              className="voyage-checkbox"
                            />
                            Primary
                          </label>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={row.can_approve === "1"}
                              onChange={(event) =>
                                handleRowChange(
                                  index,
                                  "can_approve",
                                  event.target.checked ? "1" : "0"
                                )
                              }
                              className="voyage-checkbox"
                            />
                            Approve
                          </label>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={row.can_reject === "1"}
                              onChange={(event) =>
                                handleRowChange(
                                  index,
                                  "can_reject",
                                  event.target.checked ? "1" : "0"
                                )
                              }
                              className="voyage-checkbox"
                            />
                            Reject
                          </label>
                        </div>
                      </div>

                      <div className="excavation-reviewer-card-actions">
                        <button
                          type="button"
                          className="excavation-reviewer-remove-button"
                          onClick={() => handleRemoveRow(index)}
                        >
                          <IoCloseOutline /> Remove Reviewer
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="form-group">
                    <label></label>
                    <div className="input-group excavation-reviewer-input-group">
                      <button
                        type="button"
                        className="data-edit-form-save-button"
                        onClick={handleAddRow}
                      >
                        Add Reviewer
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-edit-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading || pageLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-edit-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditExcavationReviewerConfiguration;
